import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { PaymentMethodData } from '@/types/api';

const KEY = (clientId: number) => ['payment-methods', clientId];

export function usePaymentMethods(clientId: number | undefined) {
  return useQuery({
    queryKey: clientId == null ? ['payment-methods', null] : KEY(clientId),
    enabled: clientId != null,
    queryFn: async (): Promise<PaymentMethodData[]> => {
      const { data } = await api.get<PaymentMethodData[]>(
        `/v1/clients/${clientId}/payment-methods`,
      );
      return data;
    },
  });
}

export function useCreateSetupIntent(clientId: number) {
  return useMutation({
    mutationFn: async (): Promise<{ client_secret: string; customer_id: string }> => {
      await primeCsrf();
      const { data } = await api.post<{ client_secret: string; customer_id: string }>(
        `/v1/clients/${clientId}/payment-methods/setup-intent`,
      );
      return data;
    },
  });
}

export function useSavePaymentMethod(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { payment_method_id: string; set_default?: boolean }): Promise<PaymentMethodData> => {
      await primeCsrf();
      const { data } = await api.post<PaymentMethodData>(
        `/v1/clients/${clientId}/payment-methods`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(clientId) });
      qc.invalidateQueries({ queryKey: ['clients', clientId] });
    },
  });
}

export function useSetDefaultPaymentMethod(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paymentMethodId: string): Promise<void> => {
      await primeCsrf();
      await api.post(`/v1/clients/${clientId}/payment-methods/${paymentMethodId}/default`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(clientId) });
      qc.invalidateQueries({ queryKey: ['clients', clientId] });
    },
  });
}

export function useDeletePaymentMethod(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paymentMethodId: string): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/payment-methods/${paymentMethodId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(clientId) });
      qc.invalidateQueries({ queryKey: ['clients', clientId] });
    },
  });
}
